import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Button, Input, Modal, Form, Upload, Table, Space, Breadcrumb, Tooltip,
  Select, notification, Tag
} from 'antd';
import {
  FolderAddOutlined, UploadOutlined, LeftOutlined,
  FileOutlined, FolderOutlined, ShareAltOutlined, DeleteOutlined
} from '@ant-design/icons';
import '../styles/JobPage.css';

const { Option } = Select;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

function mimeIsDoc(m) {
  return [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/plain'
  ].includes(m);
}

const JobDrive = () => {
  const [parentId, setParentId] = useState(null);
  const [items, setItems] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([]);

  // Folder modal
  const [isFolderOpen, setIsFolderOpen] = useState(false);
  const [folderForm] = Form.useForm();

  // Permissions modal (simplified)
  const [isPermOpen, setIsPermOpen] = useState(false);
  const [permTarget, setPermTarget] = useState(null);
  const [visibility, setVisibility] = useState('public'); // public | private | specific
  const [specificUsers, setSpecificUsers] = useState([]);
  const [userOptions, setUserOptions] = useState([]);

  const fetchUserOptions = async (q = '') => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/drive/user_options?q=${encodeURIComponent(q)}`,
        { withCredentials: true }
      );
      setUserOptions(res.data || []);
    } catch {
      setUserOptions([]);
    }
  };

  const loadList = async (pid) => {
    const params = new URLSearchParams();
    if (pid) params.set('parentId', pid);
    const res = await axios.get(`${API_BASE_URL}/api/drive/list?${params.toString()}`, { withCredentials: true });
    setItems(res.data || []);
  };
  const [isXL, setIsXL] = useState(() => window.innerWidth >= 1200);
    useEffect(() => {
    const onResize = () => setIsXL(window.innerWidth >= 1200);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
    }, []);
  const loadBreadcrumb = async (id) => {
    if (!id) { setBreadcrumbs([]); return; }
    const res = await axios.get(`${API_BASE_URL}/api/drive/breadcrumb/${id}`, { withCredentials: true });
    setBreadcrumbs(res.data || []);
  };

  useEffect(() => {
    loadList(parentId).catch(() => notification.error({ message: 'Failed to load drive' }));
    loadBreadcrumb(parentId).catch(() => {});
  }, [parentId]);

  const enterFolder = (record) => setParentId(record._id);

  const goUp = () => {
    if (!parentId) return;
    const prev = breadcrumbs.length > 1 ? breadcrumbs[breadcrumbs.length - 2]?._id : null;
    setParentId(prev || null);
  };

  // ------- New Folder -------
  const openCreateFolder = () => {
    folderForm.resetFields();
    setIsFolderOpen(true);
  };

  const createFolder = async () => {
    try {
      const v = await folderForm.validateFields();
      await axios.post(`${API_BASE_URL}/api/drive/folder`, { name: v.name.trim(), parentId }, { withCredentials: true });
      setIsFolderOpen(false);
      await loadList(parentId);
      notification.success({ message: 'Folder created' });
    } catch (e) {
      if (e?.errorFields) return;
      notification.error({ message: e?.response?.data?.error || 'Create failed' });
    }
  };

  // ------- Upload -------
  const beforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    const isDoc = mimeIsDoc(file.type);
    if (!isImage && !isDoc) {
      notification.error({ message: 'Only images and documents are allowed (no videos)' });
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const customRequest = async ({ file, onSuccess, onError }) => {
    try {
      const form = new FormData();
      form.append('file', file);
      if (parentId) form.append('parentId', parentId);

      const res = await axios.post(`${API_BASE_URL}/api/drive/upload`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      notification.success({ message: `Uploaded "${file.name}"` });
      onSuccess?.(res.data);
      await loadList(parentId);
    } catch (e) {
      onError?.(e);
      notification.error({ message: e?.response?.data?.error || `Upload failed: ${file.name}` });
    }
  };

  // ------- Delete -------
  const deleteItem = async (record) => {
    Modal.confirm({
      title: `Delete ${record.type}`,
      content: `Are you sure you want to delete "${record.name}"? ${record.type === 'folder' ? 'All items inside will be removed.' : ''}`,
      okType: 'danger',
      onOk: async () => {
        try {
          await axios.delete(`${API_BASE_URL}/api/drive/item/${record._id}`, { withCredentials: true });
          notification.success({ message: `Deleted "${record.name}"` });
          await loadList(parentId);
        } catch (e) {
          notification.error({ message: e?.response?.data?.error || 'Delete failed' });
        }
      }
    });
  };

  // ------- Permissions (simplified) -------
  const openPerm = async (record) => {
    setPermTarget(record);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/drive/permissions/${record._id}`, { withCredentials: true });
      const perms = res.data || [];
      const pub = perms.find(p => p.type === 'public');
      const userPerms = perms.filter(p => p.type === 'user');

      if (pub && pub.canView) {
        setVisibility('public');
        setSpecificUsers([]);
      } else if (userPerms.length) {
        setVisibility('specific');
        setSpecificUsers(userPerms.map(p => p.targetId).filter(Boolean));
      } else {
        setVisibility('private');
        setSpecificUsers([]);
      }
    } catch {
      setVisibility('public');
      setSpecificUsers([]);
    }

    fetchUserOptions('').catch(() => {});
    setIsPermOpen(true);
  };

  const savePerm = async () => {
    try {
      let permissions = [];
      if (visibility === 'public') {
        permissions.push({ type: 'public', canView: true, canUpload: true, canDownload: true, canManage: false });
      } else if (visibility === 'private') {
        permissions.push({ type: 'public', canView: false, canUpload: false, canDownload: false, canManage: false });
      } else if (visibility === 'specific') {
        permissions.push({ type: 'public', canView: false, canUpload: false, canDownload: false, canManage: false });
        for (const uid of specificUsers) {
          permissions.push({ type: 'user', targetId: uid, canView: true, canUpload: true, canDownload: true, canManage: false });
        }
      }

      await axios.post(`${API_BASE_URL}/api/drive/permissions/${permTarget._id}`, { permissions }, { withCredentials: true });
      setIsPermOpen(false);
      notification.success({ message: 'Permissions updated' });
      await loadList(parentId);
    } catch (e) {
      notification.error({ message: e?.response?.data?.error || 'Failed to update permissions' });
    }
  };

  // ------- columns (mobile-friendly) -------
  const columnsCompact = useMemo(() => ([
    {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        width: 360,
        className: 'drive-col-name',
        render: (_, r) => (
        <Space>
            {r.type === 'folder' ? <FolderOutlined /> : <FileOutlined />}
            {r.type === 'folder' ? (
            <a onClick={() => enterFolder(r)}>{r.name}</a>
            ) : (
            <span>{r.name}</span>
            )}
            {r.isImage && <Tag color="blue">image</Tag>}
        </Space>
        ),
    },
    {
        title: 'Actions',
        key: 'actions',
        width: 320,
        className: 'drive-col-actions',
        render: (_, r) => (
        <Space wrap>
            {r.type === 'file' && (
            <a
                href={`${API_BASE_URL}/api/drive/file/${r._id}`}
                onClick={() => notification.info({ message: `Preparing download: ${r.name}` })}
                target="_blank"
                rel="noreferrer"
            >
                Download
            </a>
            )}
            <Tooltip title="Share / Permissions">
            <Button size="small" icon={<ShareAltOutlined />} onClick={() => openPerm(r)} />
            </Tooltip>
            <Tooltip title="Delete">
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteItem(r)} />
            </Tooltip>
        </Space>
        ),
    },
    ]), [parentId]);
    const columnsXL = useMemo(() => ([
    {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        width: 360,
        className: 'drive-col-name',
        render: (_, r) => (
        <Space>
            {r.type === 'folder' ? <FolderOutlined /> : <FileOutlined />}
            {r.type === 'folder'
            ? <a onClick={() => enterFolder(r)}>{r.name}</a>
            : <span>{r.name}</span>}
            {r.isImage && <Tag color="blue">image</Tag>}
        </Space>
        )
    },
    {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        width: 120,
    },
    {
        title: 'Updated',
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        width: 200,
        render: (v) => v ? new Date(v).toLocaleString() : '-'
    },
    {
        title: 'Actions',
        key: 'actions',
        width: 320,
        className: 'drive-col-actions',
        render: (_, r) => (
        <Space wrap>
            {r.type === 'file' && (
            <a
                href={`${API_BASE_URL}/api/drive/file/${r._id}`}
                onClick={() => notification.info({ message: `Preparing download: ${r.name}` })}
                target="_blank"
                rel="noreferrer"
            >
                Download
            </a>
            )}
            <Tooltip title="Share / Permissions">
            <Button size="small" icon={<ShareAltOutlined />} onClick={() => openPerm(r)} />
            </Tooltip>
            <Tooltip title="Delete">
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteItem(r)} />
            </Tooltip>
        </Space>
        )
    }
    ]), [parentId]);

  return (
    <div className="drive-page">
      <div className="drive-toolbar">
        <Space wrap>
          <Button icon={<LeftOutlined />} onClick={goUp} disabled={!parentId}>Up</Button>
          <Button type="primary" icon={<FolderAddOutlined />} onClick={openCreateFolder}>New Folder</Button>
          <Upload
            multiple
            accept=".png,.jpg,.jpeg,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt"
            beforeUpload={beforeUpload}
            customRequest={customRequest}
            showUploadList={false}
          >
            <Button icon={<UploadOutlined />}>Upload (img/docs)</Button>
          </Upload>
        </Space>
        <Breadcrumb className="drive-breadcrumb">
          <Breadcrumb.Item>
            <a onClick={() => setParentId(null)}>Root</a>
          </Breadcrumb.Item>
          {breadcrumbs.map(b => (
            <Breadcrumb.Item key={b._id}>
              <a onClick={() => setParentId(b._id)}>{b.name}</a>
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>
      </div>

      {/* WRAP TABLE to guarantee horizontal scroll on very small screens */}
      <div className="drive-table-wrap">
  {isXL ? (
    <Table
      rowKey="_id"
      columns={columnsXL}
      dataSource={items}
      pagination={false}
      tableLayout="fixed"
      scroll={{ x: 'max-content' }}
    />
  ) : (
    <div className="drive-list">
        {items.map(r => (
            <div className="drive-list-item" key={r._id}>
            <div className="drive-list-name">
                {r.type === 'folder' ? <FolderOutlined /> : <FileOutlined />}
                {r.type === 'folder'
                ? <a onClick={() => enterFolder(r)}>{r.name}</a>
                : <span>{r.name}</span>}
                {r.isImage && <Tag color="blue" style={{ marginLeft: 6 }}>image</Tag>}
            </div>
            <div className="drive-list-actions">
                {r.type === 'file' && (
                <a
                    href={`${API_BASE_URL}/api/drive/file/${r._id}`}
                    onClick={() => notification.info({ message: `Preparing download: ${r.name}` })}
                    target="_blank"
                    rel="noreferrer"
                >
                    Download
                </a>
                )}
                <Tooltip title="Share / Permissions">
                <Button size="small" icon={<ShareAltOutlined />} onClick={() => openPerm(r)} />
                </Tooltip>
                <Tooltip title="Delete">
                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteItem(r)} />
                </Tooltip>
            </div>
            </div>
        ))}
        </div>
    )}
    </div>
      {/* New Folder Modal */}
      <Modal
        title="Create Folder"
        open={isFolderOpen}
        onCancel={() => setIsFolderOpen(false)}
        onOk={createFolder}
        okText="Create"
        destroyOnClose
      >
        <Form form={folderForm} layout="vertical">
          <Form.Item
            name="name"
            label="Folder Name"
            rules={[{ required: true, message: 'Enter folder name' }]}
          >
            <Input placeholder="e.g., 2025, 12345 - Project A, etc." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Permissions Modal */}
      <Modal
        title={`Permissions: ${permTarget?.name || ''}`}
        open={isPermOpen}
        onCancel={() => setIsPermOpen(false)}
        onOk={savePerm}
        okText="Save"
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <div style={{ marginBottom: 6, fontWeight: 600 }}>Visibility</div>
            <Select value={visibility} onChange={setVisibility} style={{ width: 280 }}>
              <Option value="public">Public (everyone)</Option>
              <Option value="private">Private (owner only)</Option>
              <Option value="specific">Specific people</Option>
            </Select>
          </div>

          {visibility === 'specific' && (
            <div>
              <div style={{ marginBottom: 6, fontWeight: 600 }}>People</div>
              <Select
                mode="multiple"
                style={{ width: '100%' }}
                placeholder="Search & select people"
                value={specificUsers}
                onChange={setSpecificUsers}
                onSearch={(q) => fetchUserOptions(q)}
                filterOption={false}
              >
                {userOptions.map(u => (
                  <Option key={u._id} value={u._id}>
                    {u.firstName} {u.lastName}{u.nickname ? ` (${u.nickname})` : ''} — {u.email}
                  </Option>
                ))}
              </Select>
            </div>
          )}

          <div style={{ color: '#666', fontSize: 12 }}>
            Owner always retains full access. <br />
            • Public: everyone can view, upload, and download. <br />
            • Private: only owner. <br />
            • Specific: only selected people can view, upload, and download.
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default JobDrive;
